import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook to manage pagination state in URL query parameters
 * This ensures pagination persists across page refreshes and component remounts
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.defaultPage - Default page number (default: 1)
 * @param {number} options.defaultPageSize - Default page size (default: 50)
 * @param {string} options.pageParam - URL parameter name for page (default: 'page')
 * @param {string} options.pageSizeParam - URL parameter name for page size (default: 'pageSize')
 * 
 * @returns {Object} - Pagination state and setters
 * @returns {number} currentPage - Current page number (1-indexed)
 * @returns {number} pageSize - Current page size
 * @returns {Function} setPage - Function to update page number
 * @returns {Function} setPageSize - Function to update page size
 */
export const usePaginationFromURL = ({
  defaultPage = 1,
  defaultPageSize = 50,
  pageParam = 'page',
  pageSizeParam = 'pageSize'
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse page and pageSize from URL, with fallback to defaults
  const getPageFromURL = useCallback(() => {
    const pageParamValue = searchParams.get(pageParam);
    const page = pageParamValue ? parseInt(pageParamValue, 10) : defaultPage;
    // Validate page is a positive integer
    return isNaN(page) || page < 1 ? defaultPage : page;
  }, [searchParams, pageParam, defaultPage]);

  const getPageSizeFromURL = useCallback(() => {
    const pageSizeParamValue = searchParams.get(pageSizeParam);
    const pageSize = pageSizeParamValue ? parseInt(pageSizeParamValue, 10) : defaultPageSize;
    // Validate pageSize is a positive integer
    return isNaN(pageSize) || pageSize < 1 ? defaultPageSize : pageSize;
  }, [searchParams, pageSizeParam, defaultPageSize]);

  // Initialize state from URL
  const [currentPage, setCurrentPageState] = useState(getPageFromURL);
  const [pageSize, setPageSizeState] = useState(getPageSizeFromURL);

  // Update state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const newPage = getPageFromURL();
    const newPageSize = getPageSizeFromURL();
    
    if (newPage !== currentPage) {
      setCurrentPageState(newPage);
    }
    if (newPageSize !== pageSize) {
      setPageSizeState(newPageSize);
    }
  }, [searchParams, getPageFromURL, getPageSizeFromURL, currentPage, pageSize]);

  // Update page number and URL
  const setPage = useCallback((newPage) => {
    const pageNumber = typeof newPage === 'function' ? newPage(currentPage) : newPage;
    const validPage = Math.max(1, parseInt(pageNumber, 10) || defaultPage);
    
    setCurrentPageState(validPage);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (validPage === defaultPage) {
        // Remove param if it's the default value (cleaner URLs)
        newParams.delete(pageParam);
      } else {
        newParams.set(pageParam, validPage.toString());
      }
      return newParams;
    });
  }, [currentPage, defaultPage, pageParam, setSearchParams]);

  // Update page size and URL (resets to page 1)
  const setPageSize = useCallback((newPageSize) => {
    const size = typeof newPageSize === 'function' ? newPageSize(pageSize) : newPageSize;
    const validPageSize = Math.max(1, parseInt(size, 10) || defaultPageSize);
    
    setPageSizeState(validPageSize);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (validPageSize === defaultPageSize) {
        // Remove param if it's the default value
        newParams.delete(pageSizeParam);
      } else {
        newParams.set(pageSizeParam, validPageSize.toString());
      }
      // Reset to page 1 when page size changes
      newParams.delete(pageParam);
      return newParams;
    });
    // Reset to page 1
    setCurrentPageState(defaultPage);
  }, [pageSize, defaultPageSize, defaultPage, pageSizeParam, pageParam, setSearchParams]);

  return {
    currentPage,
    pageSize,
    setPage,
    setPageSize
  };
};
